'use client';

import { Dialog, DialogContent, DialogTitle } from '@bangle.io/ui-components';
import { useAtom } from 'jotai';
import React from 'react';
import { $imageLightbox } from '../image-lightbox-state';

export type ImageLightboxProps = {
  open: boolean;
  src: string | null;
  alt?: string;
  onOpenChange: (open: boolean) => void;
};

/**
 * Full-viewport image viewer with a blurred dimmed backdrop.
 * Closes via the X button, Escape, or clicking the backdrop.
 */
export function ImageLightbox({
  open,
  src,
  alt = '',
  onOpenChange,
}: ImageLightboxProps) {
  const label = alt.trim() || 'Image';

  return (
    <Dialog open={open && Boolean(src)} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        aria-describedby={undefined}
        className="w-auto max-w-[min(96vw,72rem)] border-none bg-transparent p-2 shadow-none ring-0 sm:max-w-[min(96vw,72rem)]"
        overlayClassName="bg-black/55 supports-backdrop-filter:backdrop-blur-sm"
      >
        <DialogTitle className="sr-only">{label}</DialogTitle>
        {src ? (
          <img
            src={src}
            alt={label}
            className="mx-auto max-h-[90vh] max-w-full select-none rounded-md object-contain"
            draggable={false}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** Editor chrome: reads/writes `$imageLightbox`. */
export function EditorImageLightbox() {
  const [state, setState] = useAtom($imageLightbox);

  return (
    <ImageLightbox
      open={Boolean(state?.src)}
      src={state?.src ?? null}
      alt={state?.alt}
      onOpenChange={(next) => {
        if (!next) setState(null);
      }}
    />
  );
}
